import {
  createBaseThemeOptions,
  pageTheme as defaultPageThemes,
  PageTheme,
  palettes,
  createUnifiedTheme,
} from '@backstage/theme';

const pageThemesFontColorOverride: Record<string, PageTheme> = {};
Object.keys(defaultPageThemes).map(key => {
  pageThemesFontColorOverride[key] = {
    ...defaultPageThemes[key],
    backgroundImage: 'none',
    colors: ['rgb(249, 250, 251)', 'rgb(249, 250, 251)'], // Match sidebar background
  };
});

// Konteks Light Theme - Based on Revamp Design System
// Colors from globals.css :root (light mode)
export const konteksLightTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      primary: {
        main: '#F0A31C', // Primary gold color
        light: '#f4b24f',
        dark: '#d17f0b',
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#ececf0', // muted
        light: '#f8f8f8',
        dark: '#e9ebef', // accent
        contrastText: '#030213',
      },
      error: {
        main: '#d4183d',
        light: '#ef4444',
        dark: '#b91c1c',
      },
      warning: {
        main: '#f59e0b',
        light: '#fbbf24',
        dark: '#d97706',
      },
      success: {
        main: '#22c55e',
        light: '#4ade80',
        dark: '#16a34a',
      },
      info: {
        main: '#5292F4',
        light: '#60a5fa',
        dark: '#2563eb',
      },
      action: {
        active: '#F0A31C',
        hover: 'rgba(240, 163, 28, 0.08)',
        selected: 'rgba(240, 163, 28, 0.12)',
        disabled: 'rgba(113, 113, 130, 0.3)', // muted-foreground
        disabledBackground: 'rgba(236, 236, 240, 0.5)',
      },
      common: {
        white: '#FFFFFF',
        black: '#000000',
      },
      border: '#0000001a',
      divider: '#0000001a',
      navigation: {
        ...palettes.light.navigation,
        background: 'rgb(249, 250, 251)', // sidebar light
        color: '#1a1a1a', // sidebar-foreground
        selectedColor: '#ffffff', // sidebar-primary-foreground
        indicator: '#F0A31C', // sidebar-primary
        submenu: {
          background: 'rgb(249, 250, 251)',
        },
      },
      text: {
        primary: '#030213', // foreground - using oklch converted
        secondary: '#717182', // muted-foreground
        disabled: '#9ca3af',
        hint: '#717182',
      },
      background: {
        default: '#ffffff', // background
        paper: '#ffffff', // card
      },
      grey: {
        50: '#fafafa',
        100: '#f5f5f5',
        200: '#e5e7eb',
        300: '#d4d4d8',
        400: '#a1a1aa',
        500: '#71717a',
        600: '#52525b',
        700: '#3f3f46',
        800: '#27272a',
        900: '#18181b',
      },
    },
  }),
  typography: {
    htmlFontSize: 14, // Base font size from Revamp
    fontFamily:
      '"Geist", "Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: {
      fontFamily: '"Geist Mono", "Fira Code", monospace',
      fontSize: '1.5rem', // 24px
      fontWeight: 500,
      marginBottom: 16,
    },
    h2: {
      fontFamily: '"Geist Mono", "Fira Code", monospace',
      fontSize: '1.25rem', // 20px
      fontWeight: 500,
      marginBottom: 14,
    },
    h3: {
      fontFamily: '"Geist Mono", "Fira Code", monospace',
      fontSize: '1.125rem', // 18px
      fontWeight: 500,
      marginBottom: 12,
    },
    h4: {
      fontFamily: '"Geist", "Inter", system-ui, sans-serif',
      fontSize: '1rem', // 16px
      fontWeight: 500,
      marginBottom: 10,
    },
    h5: {
      fontFamily: '"Geist", "Inter", system-ui, sans-serif',
      fontSize: '0.875rem', // 14px
      fontWeight: 500,
      marginBottom: 8,
    },
    h6: {
      fontFamily: '"Geist", "Inter", system-ui, sans-serif',
      fontSize: '0.875rem',
      fontWeight: 500,
      marginBottom: 8,
    },
  },
  shape: {
    borderRadius: 6,
  },
  pageTheme: pageThemesFontColorOverride,
  defaultPageTheme: 'home',
  components: {
    // Global CSS baseline
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'Geist';
          src: url('https://cdn.jsdelivr.net/npm/geist@1.3.1/dist/fonts/GeistVF.woff2') format('woff2');
          font-weight: 100 900;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: 'Geist Mono';
          src: url('https://cdn.jsdelivr.net/npm/geist@1.3.1/dist/fonts/GeistMonoVF.woff2') format('woff2');
          font-weight: 100 900;
          font-style: normal;
          font-display: swap;
        }
        html {
          background-color: #ffffff;
          font-size: 14px;
        }
        body {
          background-color: #ffffff !important;
          font-feature-settings: "cv02", "cv03", "cv04", "cv11";
          margin: 0;
          padding: 0;
        }
        * {
          box-sizing: border-box;
        }
        #root {
          width: 100%;
          max-width: 100vw;
          overflow-x: hidden;
          background-color: #ffffff;
        }
        /* Fix sidebar submenu hover gap */
        [data-testid="item-with-submenu"] {
          padding-right: 4px;
        }
      `,
    },

    // Fix main content width / horizontal scroll
    BackstageContent: {
      styleOverrides: {
        root: {
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'hidden',
          padding: '24px',
        },
      },
    },
    BackstagePage: {
      styleOverrides: {
        root: {
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
        },
      },
    },
    BackstageSidebarPage: {
      styleOverrides: {
        root: {
          width: '100%',
          overflowX: 'hidden',
        },
      },
    },

    // Header with sidebar-matching background
    BackstageHeader: {
      styleOverrides: {
        header: () => ({
          background: 'rgb(249, 250, 251)', // Same as sidebar background
          paddingBottom: '12px',
          paddingTop: '12px',
          paddingLeft: '24px',
          paddingRight: '44px',
          minHeight: '70px',
          height: '70px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          boxShadow: 'none',
          margin: 0,
          borderBottom: '1px solid #e4e7eb',
        }),
        title: () => ({
          color: '#030213',
          fontFamily: '"Geist", "Inter", system-ui, sans-serif',
          fontWeight: 600,
          fontSize: '1.25rem',
          lineHeight: 1.25,
        }),
        subtitle: () => ({
          color: '#717182',
          fontFamily: '"Geist", "Inter", system-ui, sans-serif',
          fontSize: '0.875rem',
          lineHeight: 1.25,
          marginTop: '2px',
        }),
      },
    },

    BackstageHeaderLabel: {
      styleOverrides: {
        label: () => ({
          color: '#717182',
        }),
        value: () => ({
          color: '#030213',
        }),
      },
    },

    BackstageSelectInputBase: {
      styleOverrides: {
        input: () => ({
          borderColor: '#0000001a',
          backgroundColor: '#f3f3f5', // input-background
          borderRadius: '8px',
        }),
      },
    },

    // Table Overrides
    BackstageTableHeader: {
      styleOverrides: {
        header: {
          paddingTop: '16px',
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: () => ({
          '&:hover': {
            backgroundColor: '#f5f5f5',
          },
          borderTopColor: '#0000001a',
          borderTopWidth: '1px',
          borderTopStyle: 'solid',
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: () => ({
          borderBottomColor: '#0000001a',
          padding: '12px 16px !important',
        }),
        body: () => ({
          padding: '12px 16px !important',
        }),
        head: () => ({
          fontWeight: 600,
          backgroundColor: '#ffffff',
          color: '#717182',
          textTransform: 'uppercase',
          fontSize: '0.6875rem',
          letterSpacing: '0.05em',
        }),
      },
    },
    BackstageOverflowTooltip: {
      styleOverrides: {
        // @ts-expect-error typo not listed
        typo: {
          lineHeight: '1.5',
        },
      },
    },

    // Paper / Card Overrides
    MuiPaper: {
      styleOverrides: {
        root: () => ({
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: '#0000001a',
          color: '#030213',
          backgroundColor: '#ffffff',
        }),
        rounded: () => ({
          borderRadius: '8px',
          overflow: 'hidden',
        }),
        elevation0: () => ({
          boxShadow: 'none',
        }),
        elevation1: () => ({
          boxShadow: 'none',
        }),
        elevation2: () => ({
          boxShadow: 'none',
        }),
      },
    },

    // Card Overrides
    MuiCard: {
      styleOverrides: {
        root: () => ({
          backgroundColor: '#ffffff',
          borderColor: '#e8e8ee',
          borderRadius: '8px',
        }),
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: () => ({
          borderBottom: '1px solid #f3f4f6',
          padding: '10px 20px',
        }),
        title: () => ({
          fontFamily: '"Geist", "Inter", system-ui, sans-serif',
          fontSize: '13px',
          fontWeight: 600,
          letterSpacing: '-0.01em',
        }),
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: () => ({
          padding: '20px',
        }),
      },
    },

    // Button Overrides
    MuiButton: {
      styleOverrides: {
        root: () => ({
          borderRadius: '6px',
          textTransform: 'none',
          fontWeight: 500,
          padding: '8px 16px',
          fontSize: '1rem',
        }),
        containedPrimary: () => ({
          backgroundColor: '#F0A31C',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#d17f0b',
            color: '#ffffff',
          },
        }),
        outlinedPrimary: () => ({
          borderColor: '#F0A31C',
          color: '#F0A31C',
          '&:hover': {
            borderColor: '#d17f0b',
            color: '#d17f0b',
            backgroundColor: 'rgba(240, 163, 28, 0.08)',
          },
        }),
        textPrimary: () => ({
          color: '#F0A31C',
          '&:hover': {
            backgroundColor: 'rgba(240, 163, 28, 0.08)',
          },
        }),
      },
    },

    // Input / TextField Overrides
    MuiTextField: {
      styleOverrides: {
        root: () => ({
          '& .MuiOutlinedInput-root': {
            borderRadius: '6px',
            backgroundColor: '#f3f3f5',
            '& fieldset': {
              borderColor: 'transparent',
            },
            '&:hover fieldset': {
              borderColor: '#0000001a',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#F0A31C',
            },
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: () => ({
          borderRadius: '6px',
          backgroundColor: '#f3f3f5',
          minHeight: '40px',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'transparent',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0000001a',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#F0A31C',
          },
        }),
        input: () => ({
          padding: '12px 16px',
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: () => ({
          fontSize: '1rem',
          lineHeight: 1.5,
          // Add left padding when start adornment is present (underline variant)
          '&.MuiInputBase-adornedStart': {
            paddingLeft: '8px',
          },
        }),
      },
    },
    // Input adornment (icons) styling
    MuiInputAdornment: {
      styleOverrides: {
        root: () => ({
          '& .MuiSvgIcon-root': {
            fontSize: '1.25rem', // 20px - smaller icon size
            color: '#717182',
          },
        }),
        positionStart: () => ({
          paddingLeft: '8px',
        }),
      },
    },
    // Force standard (underline) inputs to use outlined styling
    MuiInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: () => ({
          borderRadius: '6px',
          backgroundColor: '#f3f3f5',
          border: '1px solid transparent',
          padding: '0',
          paddingLeft: '0',
          '&:hover': {
            borderColor: '#0000001a',
          },
          '&.Mui-focused': {
            borderColor: '#F0A31C',
          },
          '&.Mui-error': {
            borderColor: '#d4183d',
          },
          '&.Mui-disabled': {
            backgroundColor: 'rgba(236, 236, 240, 0.5)',
          },
          '&::before, &::after': {
            display: 'none',
          },
          // Add left padding when start adornment (left icon) is present
          '& .MuiInputAdornment-positionStart': {
            marginLeft: '12px',
            marginRight: '0',
          },
        }),
        input: () => ({
          padding: '12px 16px',
          '&.MuiInput-inputAdornedStart': {
            paddingLeft: '8px',
          },
        }),
      },
    },
    // Force filled inputs to use outlined styling
    MuiFilledInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: () => ({
          borderRadius: '6px',
          backgroundColor: '#f3f3f5',
          border: '1px solid transparent',
          paddingLeft: '0',
          '&:hover': {
            backgroundColor: '#f3f3f5',
            borderColor: '#0000001a',
          },
          '&.Mui-focused': {
            backgroundColor: '#f3f3f5',
            borderColor: '#F0A31C',
          },
          '&.Mui-error': {
            borderColor: '#d4183d',
          },
          '&.Mui-disabled': {
            backgroundColor: 'rgba(236, 236, 240, 0.5)',
          },
          '&::before, &::after': {
            display: 'none',
          },
          // Add left padding when start adornment (left icon) is present
          '& .MuiInputAdornment-positionStart': {
            marginLeft: '12px',
            marginRight: '0',
          },
        }),
        input: () => ({
          padding: '12px 16px',
          '&.MuiFilledInput-inputAdornedStart': {
            paddingLeft: '8px',
          },
        }),
      },
    },

    // Select Overrides
    MuiSelect: {
      styleOverrides: {
        root: () => ({
          borderRadius: '6px',
        }),
      },
    },

    // Chip Overrides
    MuiChip: {
      styleOverrides: {
        root: () => ({
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '4px',
          fontWeight: 500,
          fontSize: '0.75rem',
          margin: '2px',
        }),
        sizeSmall: {
          height: '20px',
          minWidth: '20px',
        },
        label: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 8px',
          lineHeight: 1,
        },
        labelSmall: {
          padding: '0 6px',
        },
        colorPrimary: () => ({
          backgroundColor: 'rgba(240, 163, 28, 0.15)',
          color: '#d17f0b',
          borderColor: 'rgba(240, 163, 28, 0.2)',
        }),
        colorSuccess: () => ({
          backgroundColor: 'rgba(34, 197, 94, 0.15)',
          color: '#16a34a',
        }),
        colorError: () => ({
          backgroundColor: 'rgba(212, 24, 61, 0.15)',
          color: '#d4183d',
        }),
        colorWarning: () => ({
          backgroundColor: 'rgba(245, 158, 11, 0.15)',
          color: '#d97706',
        }),
      },
    },

    // Badge Overrides
    MuiBadge: {
      styleOverrides: {
        badge: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '20px',
          height: '20px',
          padding: '0 4px',
          fontSize: '0.75rem',
          lineHeight: 1,
          fontWeight: 600,
        },
      },
    },

    // Sidebar Overrides
    BackstageSidebar: {
      styleOverrides: {
        drawer: () => ({
          backgroundColor: 'rgb(249, 250, 251)',
          borderRight: 'solid 1px #e4e7eb',
          alignItems: 'center',
          padding: '0 4px',
          top: '52px !important',
          height: 'calc(100vh - 52px) !important',
          '& > :first-child': {
            alignSelf: 'flex-start',
          },
        }),
      },
    },
    BackstageSidebarDivider: {
      styleOverrides: {
        root: () => ({
          width: '100%',
          height: '1px',
          backgroundColor: '#e4e7eb',
          border: 'none',
        }),
      },
    },
    BackstageSidebarItem: {
      styleOverrides: {
        root: () => ({
          borderRadius: '6px !important',
          margin: '2px 4px !important',
          fontSize: '0.875rem',
          height: '40px !important',
          width: 'calc(100% - 8px) !important',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
        }),
        iconContainer: () => ({
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: '0',
        }),
        submenuArrow: () => ({
          display: 'flex',
          alignItems: 'center',
          marginLeft: 'auto',
        }),
        selected: () => ({
          backgroundColor: '#e2e2e8 !important',
          borderRadius: '6px !important',
          borderLeft: 'none !important',
          color: '#000000 !important',
          margin: '2px 4px !important',
          width: 'calc(100% - 8px) !important',
          position: 'relative',
          overflow: 'visible',
          fontWeight: '600 !important',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: '0px',
            top: '20%',
            height: '60%',
            width: '2px',
            backgroundColor: '#F0A31C',
            borderRadius: '2px',
          },
          '&:hover': {
            backgroundColor: '#e2e2e8 !important',
            color: '#000000 !important',
          },
        }),
        open: () => ({
          marginTop: '2px',
          marginBottom: '2px',
          width: 'calc(100% - 8px) !important',
        }),
        closed: () => ({
          width: 'auto !important',
          margin: '2px 4px !important',
        }),
        highlightable: () => ({
          '&:hover': {
            backgroundColor: '#e2e2e8 !important',
            borderRadius: '6px !important',
            color: '#000000 !important',
          },
        }),
        highlighted: () => ({
          backgroundColor: '#e2e2e8 !important',
          color: '#000000 !important',
        }),
        label: () => ({
          fontWeight: 400,
          fontSize: '0.875rem',
        }),
        buttonItem: () => ({
          margin: '0 !important',
          padding: '0 !important',
        }),
        secondaryAction: () => ({
          marginRight: '0 !important',
        }),
      },
    },
    BackstageSidebarSubmenuItem: {
      styleOverrides: {
        selected: () => ({
          backgroundColor: '#e2e2e8',
          borderRadius: '6px',
          color: '#000000',
          '&:hover': {
            backgroundColor: '#e2e2e8',
            color: '#000000',
          },
        }),
        item: () => ({
          marginTop: 0,
          marginBottom: 0,
          paddingTop: '8px',
          paddingBottom: '8px',
          borderRadius: '6px',
          '&:hover': {
            backgroundColor: '#e2e2e8',
            color: '#000000',
          },
        }),
      },
    },
    BackstageSidebarSubmenu: {
      styleOverrides: {
        root: {
          paddingTop: '8px',
          paddingBottom: '8px',
          '& > div': {
            gap: 0,
            padding: 0,
          },
        },
      },
    },

    // Tabs Overrides
    MuiTabs: {
      styleOverrides: {
        root: () => ({
          minHeight: '42px !important',
          backgroundColor: 'rgb(249, 250, 251)',
          marginLeft: '0 !important',
        }),
        indicator: () => ({
          backgroundColor: '#F0A31C',
          height: '2px',
          borderRadius: '2px',
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: () => ({
          textTransform: 'none',
          fontWeight: '400 !important',
          fontSize: '0.8125rem !important',
          minHeight: '42px !important',
          padding: '6px 12px !important',
          color: '#717182',
          '&.Mui-selected': {
            color: '#F0A31C',
            fontWeight: '500 !important',
          },
        }),
      },
    },

    // Tooltip Overrides
    MuiTooltip: {
      styleOverrides: {
        tooltip: () => ({
          backgroundColor: '#ffffff',
          color: '#030213',
          fontSize: '0.75rem',
          borderRadius: '4px',
          padding: '6px 12px',
          border: '1px solid #0000001a',
          boxShadow: '0 4px 12px #0000001a',
        }),
      },
    },

    // Dialog Overrides
    MuiDialog: {
      styleOverrides: {
        paper: () => ({
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          border: '1px solid #0000001a',
        }),
      },
    },

    // Menu Overrides
    MuiMenu: {
      styleOverrides: {
        paper: () => ({
          backgroundColor: '#ffffff',
          borderRadius: '6px',
          border: '1px solid #0000001a',
          boxShadow: '0 4px 20px #0000001a',
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: () => ({
          fontSize: '0.875rem',
          padding: '8px 16px',
          '&:hover': {
            backgroundColor: '#f5f5f5',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(240, 163, 28, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(240, 163, 28, 0.15)',
            },
          },
        }),
      },
    },

    // Link Overrides
    MuiLink: {
      styleOverrides: {
        root: () => ({
          color: '#F0A31C',
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
            color: '#d17f0b',
          },
        }),
      },
    },

    // Breadcrumb Overrides
    MuiBreadcrumbs: {
      styleOverrides: {
        root: () => ({
          fontSize: '0.875rem',
        }),
        separator: () => ({
          color: '#717182',
        }),
      },
    },

    // Avatar Overrides
    MuiAvatar: {
      styleOverrides: {
        root: () => ({
          backgroundColor: 'rgba(240, 163, 28, 0.1)',
          color: '#F0A31C',
        }),
      },
    },

    // Divider Overrides
    MuiDivider: {
      styleOverrides: {
        root: () => ({
          backgroundColor: '#0000001a',
        }),
      },
    },

    // Skeleton Overrides
    MuiSkeleton: {
      styleOverrides: {
        root: () => ({
          backgroundColor: '#ececf0',
        }),
      },
    },

    // Alert Overrides
    MuiAlert: {
      styleOverrides: {
        root: () => ({
          borderRadius: '6px',
        }),
        standardSuccess: () => ({
          backgroundColor: '#dcfce7', // solid light green
          color: '#16a34a',
          border: '1px solid #86efac',
        }),
        standardError: () => ({
          backgroundColor: '#fee2e2', // solid light red
          color: '#d4183d',
          border: '1px solid #fca5a5',
        }),
        standardWarning: () => ({
          backgroundColor: '#fef3c7', // solid light amber
          color: '#d97706',
          border: '1px solid #fcd34d',
        }),
        standardInfo: () => ({
          backgroundColor: '#dbeafe', // solid light blue
          color: '#2563eb',
          border: '1px solid #93c5fd',
        }),
      },
    },

    // Switch Overrides
    MuiSwitch: {
      styleOverrides: {
        root: () => ({
          width: 44,
          height: 24,
          padding: 0,
        }),
        switchBase: () => ({
          padding: 2,
          '&.Mui-checked': {
            color: '#ffffff',
            '& + .MuiSwitch-track': {
              backgroundColor: '#F0A31C',
              opacity: 1,
            },
          },
        }),
        thumb: () => ({
          width: 20,
          height: 20,
        }),
        track: () => ({
          borderRadius: 12,
          backgroundColor: '#cbced4', // switch-background
          opacity: 1,
        }),
      },
    },

    // Progress Overrides
    MuiLinearProgress: {
      styleOverrides: {
        root: () => ({
          backgroundColor: '#ececf0',
          borderRadius: '4px',
          height: '6px',
        }),
        bar: () => ({
          backgroundColor: '#F0A31C',
          borderRadius: '4px',
        }),
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: () => ({
          color: '#F0A31C',
        }),
      },
    },
  },
});
